import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { sampleAccounts } from './api';
import type { SampleAccountsApi } from './index';
import { authStore } from '@/services/auth/store';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { useStore } from '@nanostores/react';

/**
 * Sample Workspace
 * interactive_demo_workspace / sample_workspace
 * Part of:
 * https://linear.app/customerio/project/sample-workspace-view-only-workspace-387ba35103c0/overview
 */

const useSampleAccountsQuery = () => {
  return useQuery({
    queryKey: ['sampleAccounts'],
    queryFn: getSampleAccounts,
  });
};

export const getSampleAccounts = async () => {
  const resp = await sampleAccounts();

  // Type guard: check if response is SampleAccountsApi
  const isSampleAccountsApi = (data: unknown): data is SampleAccountsApi => {
    return data != null && typeof data === 'object' && 'accounts' in data && Array.isArray(data.accounts);
  };

  // sampleAccounts may return undefined or an empty object from cache
  const response =
    isSampleAccountsApi(resp) && resp.accounts[0] != null ? resp.accounts[0] : { environment_ids: [], id: 0 };

  return { accountId: response.id, workspaceId: response.environment_ids[0] };
};

export const useSampleWorkspace = () => {
  const auth = useStore(authStore);
  const isBuilder = isBuilderPlan(auth.account);
  const { data: sampleAccount, isLoading, isError } = useSampleAccountsQuery();
  const sampleAccountId = isBuilder ? null : (sampleAccount?.accountId ?? null);
  const sampleWorkspaceId = isBuilder ? null : (sampleAccount?.workspaceId ?? null);

  const isSampleWorkspace = useCallback(
    (workspaceId: number | null | undefined) => {
      if (workspaceId == null) {
        return false;
      }

      return workspaceId === sampleWorkspaceId;
    },
    [sampleWorkspaceId],
  );

  return {
    sampleAccountId,
    sampleWorkspaceId,
    isSampleWorkspace,
    /**
     * While true, `isSampleWorkspace` answers false for everything — hold,
     * don't act on it. Builder plans have no sample workspace, so never load.
     */
    isLoading: isBuilder ? false : isLoading,
    /**
     * A failed sample-accounts read also leaves `isSampleWorkspace` answering
     * false for everything. Guards that must not fail open treat this like
     * the loading case: sampleness is unknown, don't act on it.
     */
    isError: isBuilder ? false : isError,
  };
};
